import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, InputGroup, Badge, Modal, Alert, Spinner, Toast, ToastContainer, Table, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faPlus, faMinus, faSearch, faBox, faWeight, faLayerGroup, faThLarge, faList, faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ExternalCheckout from './ExternalCheckout';
import './ECommerceInventory.css';
import '../styles/InfiniteTable.css';

function ECommerceInventory() {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalQuantity, setModalQuantity] = useState(1);
  const [viewMode, setViewMode] = useState(() => {
    // Initialize from localStorage or default to 'card'
    return localStorage.getItem('inventoryViewMode') || 'card';
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInventory();
    // Load cart from sessionStorage
    const savedCart = sessionStorage.getItem('externalCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  useEffect(() => {
    // Save cart to sessionStorage whenever it changes
    sessionStorage.setItem('externalCart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    // Filter and sort inventory
    let filtered = inventory;
    
    if (searchTerm) {
      filtered = inventory.filter(item =>
        item.itemNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.itemName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';
        
        // Convert to numbers for numeric columns
        if (['totalAvailable', 'piecesPerUnit'].includes(sortConfig.key)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredInventory(filtered);
  }, [inventory, searchTerm, sortConfig]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/external-orders/inventory-skus`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setInventory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setError('Failed to load inventory data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getCartQuantity = (idCatalog) => {
    const cartItem = cart.find(item => item.idCatalog === idCatalog);
    return cartItem ? cartItem.quantity : 0;
  };

  const getAvailableQuantity = (item) => {
    const cartQty = getCartQuantity(item.idCatalog);
    return Math.max(0, item.totalAvailable - cartQty);
  };

  const openQuantityModal = (item) => {
    setSelectedItem(item);
    setModalQuantity(1);
    setShowQuantityModal(true);
  };

  const addToCart = (item, quantity = 1) => {
    const available = getAvailableQuantity(item);
    
    if (available <= 0) {
      setToastMessage('No more items available');
      setShowToast(true);
      return;
    }

    const quantityToAdd = Math.min(quantity, available);
    const existingItem = cart.find(cartItem => cartItem.idCatalog === item.idCatalog);

    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.idCatalog === item.idCatalog
          ? { ...cartItem, quantity: existingItem.quantity + quantityToAdd }
          : cartItem
      ));
    } else {
      setCart([...cart, {
        idCatalog: item.idCatalog,
        itemNumber: item.itemNumber,
        itemName: item.itemName,
        quantity: quantityToAdd,
        unitWeight: item.avgWeight || 0,
        totalWeight: (item.avgWeight || 0) * quantityToAdd
      }]);
    }

    setToastMessage(`Added ${quantityToAdd} ${item.itemName}${quantityToAdd > 1 ? 's' : ''} to cart`);
    setShowToast(true);
  };

  const handleQuantityModalConfirm = () => {
    if (selectedItem && modalQuantity > 0) {
      addToCart(selectedItem, modalQuantity);
    }
    setShowQuantityModal(false);
    setSelectedItem(null);
    setModalQuantity(1);
  };

  const updateCartQuantity = (idCatalog, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(idCatalog);
      return;
    }

    const item = inventory.find(inv => inv.idCatalog === idCatalog);
    if (!item) return;

    const maxQuantity = item.totalAvailable;
    const finalQuantity = Math.min(newQuantity, maxQuantity);

    setCart(cart.map(cartItem =>
      cartItem.idCatalog === idCatalog
        ? { 
            ...cartItem, 
            quantity: finalQuantity,
            totalWeight: cartItem.unitWeight * finalQuantity
          }
        : cartItem
    ));
  };

  const removeFromCart = (idCatalog) => {
    setCart(cart.filter(item => item.idCatalog !== idCatalog));
    setToastMessage('Item removed from cart');
    setShowToast(true);
  };

  const getTotalCartItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleOrderComplete = (result) => {
    // Clear cart
    setCart([]);
    sessionStorage.removeItem('externalCart');
    
    setShowCheckout(false);
    setToastMessage(`Order ${result.orderNumber} submitted successfully for approval!`);
    setShowToast(true);
    
    // Navigate to external orders page (we'll create this next)
    setTimeout(() => {
      navigate('/external-orders');
    }, 2000);
  };

  const handleCheckoutCancel = () => {
    setShowCheckout(false);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('inventoryViewMode', mode);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return faSort;
    }
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const renderTableView = () => {
    return (
      <div 
        style={{
          height: 'calc(100vh - 200px)',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          overflow: 'hidden',
          background: 'white'
        }}
      >
        <div 
          style={{
            height: '100%',
            overflowY: 'auto',
            overflowX: 'auto'
          }}
        >
          <table 
            style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              margin: 0
            }}
          >
            <thead>
              <tr>
                <th 
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                    padding: '12px 15px',
                    borderBottom: '2px solid #dee2e6',
                    textAlign: 'left',
                    fontWeight: '600',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '0.9rem',
                    color: '#495057'
                  }}
                  onClick={() => handleSort('itemNumber')}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Item Number</span>
                    <FontAwesomeIcon icon={getSortIcon('itemNumber')} style={{ marginLeft: '8px', opacity: 0.7 }} />
                  </div>
                </th>
                <th 
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                    padding: '12px 15px',
                    borderBottom: '2px solid #dee2e6',
                    textAlign: 'left',
                    fontWeight: '600',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '0.9rem',
                    color: '#495057'
                  }}
                  onClick={() => handleSort('itemName')}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Item Name</span>
                    <FontAwesomeIcon icon={getSortIcon('itemName')} style={{ marginLeft: '8px', opacity: 0.7 }} />
                  </div>
                </th>
                <th 
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                    padding: '12px 15px',
                    borderBottom: '2px solid #dee2e6',
                    textAlign: 'right',
                    fontWeight: '600',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '0.9rem',
                    color: '#495057'
                  }}
                  onClick={() => handleSort('piecesPerUnit')}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Pieces Each</span>
                    <FontAwesomeIcon icon={getSortIcon('piecesPerUnit')} style={{ marginLeft: '8px', opacity: 0.7 }} />
                  </div>
                </th>
                <th 
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                    padding: '12px 15px',
                    borderBottom: '2px solid #dee2e6',
                    textAlign: 'right',
                    fontWeight: '600',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '0.9rem',
                    color: '#495057'
                  }}
                  onClick={() => handleSort('totalAvailable')}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Available</span>
                    <FontAwesomeIcon icon={getSortIcon('totalAvailable')} style={{ marginLeft: '8px', opacity: 0.7 }} />
                  </div>
                </th>
                <th 
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                    padding: '12px 15px',
                    borderBottom: '2px solid #dee2e6',
                    textAlign: 'right',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    color: '#495057'
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredInventory.map((item, index) => (
                <tr 
                  key={item.idCatalog}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                  }}
                >
                  <td style={{ padding: '8px 15px', borderBottom: '1px solid #e8ecef' }}>
                    <strong>{item.itemNumber}</strong>
                  </td>
                  <td style={{ padding: '8px 15px', borderBottom: '1px solid #e8ecef' }}>
                    {item.itemName}
                  </td>
                  <td style={{ padding: '8px 15px', borderBottom: '1px solid #e8ecef', textAlign: 'right' }}>
                    <span style={{ fontWeight: 'bold' }}>{item.piecesPerUnit || '-'}</span>
                  </td>
                  <td style={{ padding: '8px 15px', borderBottom: '1px solid #e8ecef', textAlign: 'right' }}>
                    <span style={{ fontWeight: 'bold' }}>{item.totalAvailable || 0}</span>
                  </td>
                  <td style={{ padding: '8px 15px', borderBottom: '1px solid #e8ecef', textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                      {getCartQuantity(item.idCatalog) > 0 && (
                        <Badge bg="success" className="status-badge">
                          {getCartQuantity(item.idCatalog)}
                        </Badge>
                      )}
                      <Button
                        variant="primary"
                        size="sm"
                        className="table-action-btn"
                        onClick={() => openQuantityModal(item)}
                        disabled={item.totalAvailable <= 0}
                        title="Add to Cart"
                      >
                        <FontAwesomeIcon icon={faShoppingCart} className="me-1" />
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredInventory.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
              No products found matching your search.
            </div>
          )}
        </div>
      </div>
    );
  };

  if (showCheckout) {
    return (
      <ExternalCheckout 
        cart={cart}
        onOrderComplete={handleOrderComplete}
        onCancel={handleCheckoutCancel}
      />
    );
  }

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status" size="lg">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading inventory...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="ecommerce-inventory">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">Available Products</h2>
            <Button 
              variant="primary" 
              className="cart-button"
              onClick={() => setShowCart(true)}
              disabled={cart.length === 0}
            >
              <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
              Cart ({getTotalCartItems()})
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={6} className="d-flex justify-content-end">
          <ButtonGroup>
            <Button
              variant={viewMode === 'card' ? 'primary' : 'outline-primary'}
              onClick={() => handleViewModeChange('card')}
              size="sm"
            >
              <FontAwesomeIcon icon={faThLarge} className="me-1" />
              Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
              onClick={() => handleViewModeChange('table')}
              size="sm"
            >
              <FontAwesomeIcon icon={faList} className="me-1" />
              Table
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      {filteredInventory.length === 0 ? (
        <Alert variant="info">
          No products found matching your search.
        </Alert>
      ) : viewMode === 'card' ? (
        <div 
          style={{
            height: 'calc(100vh - 200px)',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <Row>
            {filteredInventory.map((item) => (
              <Col key={item.idCatalog} lg={4} md={6} className="mb-4">
                <Card className="product-card h-100 shadow-sm">
                  <Card.Body className="d-flex flex-column">
                    <div className="product-header mb-3">
                      <h5 className="product-title">{item.itemNumber}</h5>
                      <p className="product-name text-muted">{item.itemName}</p>
                    </div>

                    <div className="product-stats mb-3">
                      <Row>
                        <Col xs={6} className="text-center">
                          <div className="stat-item">
                            <FontAwesomeIcon icon={faBox} className="text-primary mb-1" />
                            <div className="stat-value">{item.totalAvailable || 0}</div>
                            <small className="text-muted">Available</small>
                          </div>
                        </Col>
                        <Col xs={6} className="text-center">
                          <div className="stat-item">
                            <FontAwesomeIcon icon={faLayerGroup} className="text-info mb-1" />
                            <div className="stat-value">{item.piecesPerUnit || '-'}</div>
                            <small className="text-muted">Pieces Each</small>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="mt-auto">
                      {getCartQuantity(item.idCatalog) > 0 ? (
                        <div className="quantity-controls">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="text-muted">In Cart: {getCartQuantity(item.idCatalog)}</small>
                            <small className="text-muted">Available: {getAvailableQuantity(item)}</small>
                          </div>
                          <div className="d-flex">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => updateCartQuantity(item.idCatalog, getCartQuantity(item.idCatalog) - 1)}
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </Button>
                            <Form.Control
                              type="number"
                              size="sm"
                              className="text-center mx-2"
                              style={{ maxWidth: '80px' }}
                              value={getCartQuantity(item.idCatalog)}
                              onChange={(e) => updateCartQuantity(item.idCatalog, parseInt(e.target.value) || 0)}
                              min="0"
                              max={item.totalAvailable}
                            />
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => updateCartQuantity(item.idCatalog, getCartQuantity(item.idCatalog) + 1)}
                              disabled={getAvailableQuantity(item) <= 0}
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          variant="primary"
                          className="w-100"
                          onClick={() => addToCart(item)}
                          disabled={item.totalAvailable <= 0}
                        >
                          <FontAwesomeIcon icon={faPlus} className="me-2" />
                          Add to Cart
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      ) : (
        renderTableView()
      )}

      {/* Quantity Selection Modal */}
      <Modal show={showQuantityModal} onHide={() => setShowQuantityModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add to Cart</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <div>
              <div className="mb-3">
                <h6 className="mb-1">{selectedItem.itemNumber}</h6>
                <p className="text-muted mb-2">{selectedItem.itemName}</p>
                
                {/* PPU Information */}
                <div className="mb-3 p-3 bg-light rounded">
                  <div className="row">
                    <div className="col-6">
                      <small className="text-muted d-block">Pieces Per Unit</small>
                      <strong className="text-info">
                        {selectedItem.piecesPerUnit || 'N/A'} pieces
                      </strong>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Available Units</small>
                      <strong>{getAvailableQuantity(selectedItem)} units</strong>
                    </div>
                  </div>
                </div>

                {getCartQuantity(selectedItem.idCatalog) > 0 && (
                  <div className="mb-3">
                    <small className="text-muted">
                      Currently in cart: <strong>{getCartQuantity(selectedItem.idCatalog)} units</strong>
                      {selectedItem.piecesPerUnit && (
                        <span> ({getCartQuantity(selectedItem.idCatalog) * selectedItem.piecesPerUnit} total pieces)</span>
                      )}
                    </small>
                  </div>
                )}
              </div>
              
              <div className="mb-3">
                <Form.Label>Number of units to add:</Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setModalQuantity(Math.max(1, modalQuantity - 1))}
                    disabled={modalQuantity <= 1}
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </Button>
                  <Form.Control
                    type="number"
                    value={modalQuantity}
                    onChange={(e) => setModalQuantity(Math.max(1, Math.min(getAvailableQuantity(selectedItem), parseInt(e.target.value) || 1)))}
                    min="1"
                    max={getAvailableQuantity(selectedItem)}
                    style={{ width: '80px', textAlign: 'center' }}
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setModalQuantity(Math.min(getAvailableQuantity(selectedItem), modalQuantity + 1))}
                    disabled={modalQuantity >= getAvailableQuantity(selectedItem)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </Button>
                </div>
                
                {/* Total Pieces Calculation */}
                {selectedItem.piecesPerUnit && (
                  <div className="mt-2 p-2 bg-success bg-opacity-10 rounded">
                    <small className="text-success d-block">
                      <FontAwesomeIcon icon={faBox} className="me-1" />
                      Adding <strong>{modalQuantity} units</strong> × <strong>{selectedItem.piecesPerUnit} pieces/unit</strong> = 
                      <strong className="ms-1">{modalQuantity * selectedItem.piecesPerUnit} total pieces</strong>
                    </small>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuantityModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleQuantityModalConfirm}
            disabled={!selectedItem || modalQuantity <= 0 || getAvailableQuantity(selectedItem) <= 0}
          >
            <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
            Add {modalQuantity} Unit{modalQuantity > 1 ? 's' : ''} to Cart
            {selectedItem && selectedItem.piecesPerUnit && (
              <small className="d-block" style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                ({modalQuantity * selectedItem.piecesPerUnit} total pieces)
              </small>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cart Modal */}
      <Modal show={showCart} onHide={() => setShowCart(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Shopping Cart</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cart.length === 0 ? (
            <p>Your cart is empty.</p>
          ) : (
            <div>
              {cart.map((item) => (
                <div key={item.idCatalog} className="cart-item d-flex justify-content-between align-items-center mb-3 p-3 border rounded">
                  <div>
                    <h6 className="mb-1">{item.itemNumber}</h6>
                    <small className="text-muted">{item.itemName}</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="me-3">Qty: {item.quantity}</span>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeFromCart(item.idCatalog)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
              <hr />
              <div className="d-flex justify-content-between">
                <strong>Total Items: {getTotalCartItems()}</strong>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCart(false)}>
            Continue Shopping
          </Button>
          {cart.length > 0 && (
            <Button 
              variant="primary" 
              onClick={() => {
                setShowCart(false);
                setShowCheckout(true);
              }}
            >
              Proceed to Checkout
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Toast notifications */}
      <ToastContainer position="top-end" className="position-fixed" style={{ zIndex: 9999 }}>
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
}

export default ECommerceInventory; 